import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getLessons, addLesson, deleteLesson, updateLesson } from '../../actions/lessonActions';
import { getCourses, addCourse, deleteCourse, updateCourse } from '../../actions/courseActions';
import { getPaths, addPath, deletePath, updatePath } from '../../actions/pathActions';
import { getQuestions, addQuestion, deleteQuestion, updateQuestion } from '../../actions/questionActions';
import { getEvents, addEvent, deleteEvent, updateEvent } from '../../actions/eventActions';
import { getPrograms } from '../../actions/programActions';
import { getSubmissionForms, addSubmissionForm, deleteSubmissionForm, updateSubmissionForm } from '../../actions/submissionFormActions';
import { getSubmissions, addSubmission, deleteSubmission, updateSubmission, reviewSubmission, setSubmissionsPage } from '../../actions/submissionActions';
import { getTags, addTag, deleteTag, updateTag } from '../../actions/tagActions';
import { getReinforcements, addReinforcement, deleteReinforcement, updateReinforcement } from '../../actions/reinforcementActions';
import ContentManagementSidebar from './ContentManagementSidebar';
import LessonForm from './LessonForm';
import CourseForm from './CourseForm';
import QuestionForm from './QuestionForm';
import EventForm from './EventForm';
import SubmissionFormForm from './SubmissionFormForm';
import SubmissionForm from './SubmissionForm';
import TagForm from './TagForm';
import ReinforcementForm from './ReinforcementForm';
import TableView from './views/TableView';
import CardView from './views/CardView';
import MobileView from './views/MobileView';
import TiersTab from '../settings/TiersTab';
import DeleteConfirmationModal from './common/DeleteConfirmationModal';
import PaginationControls from '../common/PaginationControls';
import { Plus, LayoutGrid, Table2, Book } from 'lucide-react';
import { isAdminRole } from '../../utils/roleHelpers';

const ContentManagementContainer = ({
  auth: { user },
  lesson: { lessons, loading },
  course: { courses, loading: coursesLoading },
  path: { paths, loading: pathsLoading },
  question: { questions, loading: questionsLoading },
  event: { events, loading: eventsLoading },
  program: { programs, loading: programsLoading },
  submissionForm: { submissionForms, loading: submissionFormsLoading },
  submission: { submissions, loading: submissionsLoading, currentPage: submissionsCurrentPage, totalPages: submissionsTotalPages, totalSubmissions, hasNextPage: submissionsHasNextPage, hasPrevPage: submissionsHasPrevPage },
  tag: { tags, loading: tagsLoading },
  reinforcement: { reinforcements, loading: reinforcementsLoading },
  getLessons,
  addLesson,
  deleteLesson,
  updateLesson,
  getCourses,
  addCourse,
  deleteCourse,
  updateCourse,
  getPaths,
  addPath,
  deletePath,
  updatePath,
  getQuestions,
  addQuestion,
  deleteQuestion,
  updateQuestion,
  getEvents,
  addEvent,
  deleteEvent,
  updateEvent,
  getPrograms,
  getSubmissionForms,
  addSubmissionForm,
  deleteSubmissionForm,
  updateSubmissionForm,
  getSubmissions,
  addSubmission,
  deleteSubmission,
  updateSubmission,
  reviewSubmission,
  setSubmissionsPage,
  getTags,
  addTag,
  deleteTag,
  updateTag,
  getReinforcements,
  addReinforcement,
  deleteReinforcement,
  updateReinforcement
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'Courses');
  const [formMode, setFormMode] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedTagFilters, setSelectedTagFilters] = useState([]);
  const [selectedCourseTagFilters, setSelectedCourseTagFilters] = useState([]);
  const [selectedPathTagFilters, setSelectedPathTagFilters] = useState([]);
  const [selectedQuestionTagFilters, setSelectedQuestionTagFilters] = useState([]);
  const [selectedSubmissionFormTagFilters, setSelectedSubmissionFormTagFilters] = useState([]);
  const [selectedSubmissionFormFilters, setSelectedSubmissionFormFilters] = useState([]);
  const [selectedSubmissionStatusFilters, setSelectedSubmissionStatusFilters] = useState([]);
  const [selectedEventProgramFilters, setSelectedEventProgramFilters] = useState([]);
  const [selectedEventStatusFilters, setSelectedEventStatusFilters] = useState('active');
  const [selectedReinforcementTagFilters, setSelectedReinforcementTagFilters] = useState([]);
  
  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';
  
  // Determine if current tab is read-only for instructors
  const isReadOnly = isInstructor && ['Lessons', 'Courses', 'Paths', 'Reinforcements'].includes(activeTab);

  // Clear location state after using it
  useEffect(() => {
    if (location.state?.activeTab) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load shared data needed across tabs (tags for filters, programs for dropdowns)
  useEffect(() => {
    if (isAdmin || isInstructor) {
      getTags();
      getPrograms();
    }
  }, [isAdmin, isInstructor, getTags, getPrograms]);

  // Submissions pagination effect
  useEffect(() => {
    if (activeTab === 'Submissions' && isAdmin) {
      const filters = {};
      if (selectedSubmissionFormFilters.length > 0) {
        filters.formId = selectedSubmissionFormFilters;
      }
      if (selectedSubmissionStatusFilters.length > 0) {
        filters.status = selectedSubmissionStatusFilters;
      }
      getSubmissions(submissionsCurrentPage, 10, filters);
    }
  }, [activeTab, submissionsCurrentPage, selectedSubmissionFormFilters, selectedSubmissionStatusFilters, isAdmin, getSubmissions]);

  // Refresh data when switching tabs
  useEffect(() => {
    switch (activeTab) {
      case 'Lessons':
        getLessons();
        break;
      case 'Courses':
        getCourses();
        break;
      case 'Paths':
        getPaths();
        break;
      case 'Questions':
        if (isAdmin) getQuestions();
        break;
      case 'Events & Information':
        getEvents();
        break;
      case 'Submission Forms':
        getSubmissionForms();
        break;
      case 'Submissions':
        // Handled by separate pagination effect
        break;
      case 'Reinforcements':
        if (isAdmin || isInstructor) getReinforcements();
        break;
      case 'Tags':
        if (isAdmin || isInstructor) getTags();
        break;
      default:
        break;
    }
  }, [activeTab, isAdmin, getLessons, getCourses, getPaths, getQuestions, getEvents, getSubmissionForms, getSubmissions, getTags, getReinforcements]);

  const handleAdd = () => {
    if (isReadOnly) return; // Prevent add in read-only mode
    
    if (activeTab === 'Paths') {
      navigate('/paths/new');
    } else if (activeTab === 'Submissions') {
      // Submissions are student-only, no manual creation for admins/instructors
      return;
    } else {
      setFormMode('new');
      setSelectedItem(null);
      setShowForm(true);
    }
  };

  const handleView = (item) => {
    if (activeTab === 'Paths') {
      navigate(`/paths/${item._id}/view`);
    } else {
      setFormMode('view');
      setSelectedItem(item);
      setShowForm(true);
    }
  };

  const handleEdit = (item) => {
    if (isReadOnly) return; // Prevent edit in read-only mode
    
    if (activeTab === 'Paths') {
      navigate(`/paths/${item._id}/edit`);
    } else {
      setFormMode('edit');
      setSelectedItem(item);
      setShowForm(true);
    }
  };

  const handleDeleteClick = (item) => {
    if (isReadOnly) return; // Prevent delete in read-only mode
    
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    switch (activeTab) {
      case 'Lessons':
        deleteLesson(itemToDelete._id);
        break;
      case 'Courses':
        deleteCourse(itemToDelete._id);
        break;
      case 'Paths':
        deletePath(itemToDelete._id);
        break;
      case 'Questions':
        deleteQuestion(itemToDelete._id);
        break;
      case 'Events & Information':
        deleteEvent(itemToDelete._id);
        break;
      case 'Submission Forms':
        deleteSubmissionForm(itemToDelete._id);
        break;
      case 'Submissions':
        deleteSubmission(itemToDelete._id);
        break;
      case 'Reinforcements':
        deleteReinforcement(itemToDelete._id);
        break;
      case 'Tags':
        deleteTag(itemToDelete._id);
        break;
      default:
        break;
    }
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedItem(null);
    setFormMode(null);
  };

  const handleFormSubmit = (data) => {
    switch (activeTab) {
      case 'Lessons':
        if (formMode === 'edit') {
          updateLesson(selectedItem._id, data);
        } else {
          addLesson(data);
        }
        break;
      case 'Courses':
        if (formMode === 'edit') {
          updateCourse(selectedItem._id, data);
        } else {
          addCourse(data);
        }
        break;
      case 'Paths':
        if (formMode === 'edit') {
          updatePath(selectedItem._id, data);
        } else {
          addPath(data);
        }
        break;
      case 'Questions':
        if (formMode === 'edit') {
          updateQuestion(selectedItem._id, data);
        } else {
          addQuestion(data);
        }
        break;
      case 'Events & Information':
        if (formMode === 'edit') {
          return updateEvent(selectedItem._id, data);
        } else {
          return addEvent(data);
        }
      case 'Submission Forms':
        if (formMode === 'edit') {
          updateSubmissionForm(selectedItem._id, data);
        } else {
          addSubmissionForm(data);
        }
        break;
      case 'Submissions':
        if (formMode === 'edit') {
          // Use reviewSubmission for status updates, updateSubmission for other changes
          if (data.status && data.status !== selectedItem.status) {
            reviewSubmission(selectedItem._id, data);
          } else {
            updateSubmission(selectedItem._id, data);
          }
        } else {
          addSubmission(data);
        }
        break;
      case 'Reinforcements':
        if (formMode === 'edit') {
          updateReinforcement(selectedItem._id, data);
        } else {
          addReinforcement(data);
        }
        break;
      case 'Tags':
        if (formMode === 'edit') {
          updateTag(selectedItem._id, data);
        } else {
          addTag(data);
        }
        break;
      default:
        break;
    }
    handleFormClose();
  };

  const getItemTitle = (item) => {
    if (activeTab === 'Questions') {
      return item.text;
    }
    if (activeTab === 'Submission Forms') {
      return item.title;
    }
    if (activeTab === 'Submissions') {
      // Return just the student name for the title column
      return item.userId?.name || 'Unknown Student';
    }
    return item.title;
  };

  const getColumnTitle = () => {
    if (activeTab === 'Questions') {
      return 'Question Text';
    }
    if (activeTab === 'Submission Forms') {
      return 'Form Title';
    }
    if (activeTab === 'Submissions') {
      return 'Student - Form';
    }
    return 'Title';
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getThumbnailUrl = (thumbnail) => {
    if (!thumbnail) return null;
    if (thumbnail.url) return thumbnail.url;
    if (thumbnail.data && thumbnail.contentType) return `data:${thumbnail.contentType};base64,${thumbnail.data}`;
    return null;
  };

  // Handle submissions filter changes with page reset
  const handleSubmissionFormFiltersChange = (newFilters) => {
    setSelectedSubmissionFormFilters(newFilters);
    setSubmissionsPage(1); // Reset to page 1 when filters change
  };

  const handleSubmissionStatusFiltersChange = (newFilters) => {
    setSelectedSubmissionStatusFilters(newFilters);
    setSubmissionsPage(1); // Reset to page 1 when filters change
  };

  const renderContent = () => {
    // Redirect to dedicated Lessons page
    if (activeTab === 'Lessons') {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Book className="w-16 h-16 text-[#1086a4] mb-4" />
          <h3 className="text-xl font-semibold text-[#054581] mb-2">
            Lessons Management
          </h3>
          <p className="text-gray-600 mb-4 text-center max-w-md">
            Please use the dedicated Lessons page for lesson management.
          </p>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Lessons' } })}
            className="bg-[#1086a4] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#054581] transition-colors"
          >
            Go to Lessons
          </button>
        </div>
      );
    }

    // Redirect to dedicated Courses page
    if (activeTab === 'Courses') {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Book className="w-16 h-16 text-[#1086a4] mb-4" />
          <h3 className="text-xl font-semibold text-[#054581] mb-2">
            Courses Management
          </h3>
          <p className="text-gray-600 mb-4 text-center max-w-md">
            Please use the dedicated Courses page for course management.
          </p>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Courses' } })}
            className="bg-[#1086a4] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#054581] transition-colors"
          >
            Go to Courses
          </button>
        </div>
      );
    }

    // Redirect to dedicated Submissions page
    if (activeTab === 'Submissions') {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Book className="w-16 h-16 text-[#1086a4] mb-4" />
          <h3 className="text-xl font-semibold text-[#054581] mb-2">
            Submissions Management
          </h3>
          <p className="text-gray-600 mb-4 text-center max-w-md">
            Please use the dedicated Submissions page for submission management.
          </p>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Submissions' } })}
            className="bg-[#1086a4] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#054581] transition-colors"
          >
            Go to Submissions
          </button>
        </div>
      );
    }

    // Redirect to dedicated Paths page
    if (activeTab === 'Paths') {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Book className="w-16 h-16 text-[#1086a4] mb-4" />
          <h3 className="text-xl font-semibold text-[#054581] mb-2">
            Paths Management
          </h3>
          <p className="text-gray-600 mb-4 text-center max-w-md">
            Please use the dedicated Paths page for path management.
          </p>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Paths' } })}
            className="bg-[#1086a4] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#054581] transition-colors"
          >
            Go to Paths
          </button>
        </div>
      );
    }

    // Tiers tab — rendered inline
    if (activeTab === 'Tiers') {
      return <TiersTab />;
    }

    const isLoading = activeTab === 'Courses' ? coursesLoading :
                     activeTab === 'Paths' ? pathsLoading :
                     activeTab === 'Events & Information' ? eventsLoading :
                     activeTab === 'Submission Forms' ? submissionFormsLoading :
                     activeTab === 'Submissions' ? submissionsLoading :
                     activeTab === 'Reinforcements' ? reinforcementsLoading :
                     activeTab === 'Tags' ? tagsLoading :
                     questionsLoading;

    let items = activeTab === 'Lessons' ? lessons :
                activeTab === 'Courses' ? courses :
                activeTab === 'Paths' ? paths :
                activeTab === 'Events & Information' ? events :
                activeTab === 'Submission Forms' ? submissionForms :
                activeTab === 'Submissions' ? submissions :
                activeTab === 'Reinforcements' ? reinforcements :
                activeTab === 'Tags' ? tags :
                questions;

    // Ensure items is always an array
    if (!Array.isArray(items)) items = [];

    // Filter lessons by selected tags
    if (activeTab === 'Lessons' && selectedTagFilters.length > 0 && items) {
      items = items.filter(lesson => 
        lesson.tags && lesson.tags.some(tag => selectedTagFilters.includes(tag._id))
      );
    }

    // Filter courses by selected tags
    if (activeTab === 'Courses' && selectedCourseTagFilters.length > 0 && items) {
      items = items.filter(course => 
        course.tags && course.tags.some(tag => selectedCourseTagFilters.includes(tag._id))
      );
    }

    // Filter paths by selected tags
    if (activeTab === 'Paths' && selectedPathTagFilters.length > 0 && items) {
      items = items.filter(path => 
        path.tags && path.tags.some(tag => selectedPathTagFilters.includes(tag._id))
      );
    }

    // Filter questions by selected tags
    if (activeTab === 'Questions' && selectedQuestionTagFilters.length > 0 && items) {
      items = items.filter(question => 
        question.tags && question.tags.some(tag => selectedQuestionTagFilters.includes(tag._id))
      );
    }

    // Filter submission forms by selected tags
    if (activeTab === 'Submission Forms' && selectedSubmissionFormTagFilters.length > 0 && items) {
      items = items.filter(submissionForm =>
        submissionForm.tags && submissionForm.tags.some(tag => selectedSubmissionFormTagFilters.includes(tag._id))
      );
    }

    // Note: Submissions filtering is handled server-side via API filters (no client-side filtering)

    // Filter events by selected programs
    if (activeTab === 'Events & Information' && selectedEventProgramFilters.length > 0 && items) {
      items = items.filter(event => 
        event.program && selectedEventProgramFilters.includes(event.program._id)
      );
    }

    // Filter reinforcements by selected tags
    if (activeTab === 'Reinforcements' && selectedReinforcementTagFilters.length > 0 && items) {
      items = items.filter(reinforcement => 
        reinforcement.tags && reinforcement.tags.some(tag => selectedReinforcementTagFilters.includes(tag._id))
      );
    }

    // Filter events by status
    if (activeTab === 'Events & Information' && items) {
      items = items.filter(event => {
        if (selectedEventStatusFilters === 'active') {
          return event.isActive !== false;
        } else if (selectedEventStatusFilters === 'deactivated') {
          return event.isActive === false;
        }
        return true; // 'all' or any other value shows all
      });
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-[#054581]">
              Manage {activeTab}
            </h2>
            {isReadOnly && (
              <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full">
                Read Only
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* View Toggle - Only show for Courses and Events in desktop view */}
            {(activeTab === 'Courses' || activeTab === 'Events & Information') && (
              <div className="hidden md:flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white text-[#1086a4] shadow-sm'
                      : 'text-gray-600 hover:text-[#1086a4]'
                  }`}
                  title="Table View"
                >
                  <Table2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-white text-[#1086a4] shadow-sm'
                      : 'text-gray-600 hover:text-[#1086a4]'
                  }`}
                  title="Card View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            )}
            {activeTab !== 'Submissions' && !isReadOnly && (
              <button
                onClick={handleAdd}
                className="bg-[#1086a4] text-white px-2 sm:px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span>Add</span>
              </button>
            )}
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          <MobileView
            items={items}
            activeTab={activeTab}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            getThumbnailUrl={getThumbnailUrl}
            truncateText={truncateText}
            getItemTitle={getItemTitle}
            isReadOnly={isReadOnly}
          />
        </div>

        {/* Desktop View */}
        <div className="hidden md:block">
          {(activeTab === 'Courses' || activeTab === 'Events & Information') && viewMode === 'cards' ? (
            <CardView
              items={items}
              activeTab={activeTab}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              getThumbnailUrl={getThumbnailUrl}
              truncateText={truncateText}
              getItemTitle={getItemTitle}
              isReadOnly={isReadOnly}
            />
          ) : (
            <TableView
              items={items}
              activeTab={activeTab}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              getThumbnailUrl={getThumbnailUrl}
              truncateText={truncateText}
              getItemTitle={getItemTitle}
              isReadOnly={isReadOnly}
              availableTags={(activeTab === 'Lessons' || activeTab === 'Courses' || activeTab === 'Paths' || activeTab === 'Questions' || activeTab === 'Submission Forms' || activeTab === 'Reinforcements') ? tags : null}
              selectedTagFilters={
                activeTab === 'Lessons' ? selectedTagFilters :
                activeTab === 'Courses' ? selectedCourseTagFilters :
                activeTab === 'Paths' ? selectedPathTagFilters :
                activeTab === 'Questions' ? selectedQuestionTagFilters :
                activeTab === 'Submission Forms' ? selectedSubmissionFormTagFilters :
                activeTab === 'Reinforcements' ? selectedReinforcementTagFilters : []
              }
              onTagFiltersChange={
                activeTab === 'Lessons' ? setSelectedTagFilters :
                activeTab === 'Courses' ? setSelectedCourseTagFilters :
                activeTab === 'Paths' ? setSelectedPathTagFilters :
                activeTab === 'Questions' ? setSelectedQuestionTagFilters :
                activeTab === 'Submission Forms' ? setSelectedSubmissionFormTagFilters :
                activeTab === 'Reinforcements' ? setSelectedReinforcementTagFilters : () => {}
              }
              availablePrograms={activeTab === 'Events & Information' ? programs : null}
              selectedProgramFilters={activeTab === 'Events & Information' ? selectedEventProgramFilters : []}
              onProgramFiltersChange={activeTab === 'Events & Information' ? setSelectedEventProgramFilters : () => {}}
              selectedEventStatusFilters={activeTab === 'Events & Information' ? selectedEventStatusFilters : 'active'}
              onEventStatusFiltersChange={activeTab === 'Events & Information' ? setSelectedEventStatusFilters : () => {}}
              availableSubmissionForms={activeTab === 'Submissions' ? submissionForms : null}
              selectedSubmissionFormFilters={activeTab === 'Submissions' ? selectedSubmissionFormFilters : []}
              onSubmissionFormFiltersChange={activeTab === 'Submissions' ? handleSubmissionFormFiltersChange : () => {}}
              selectedSubmissionStatusFilters={activeTab === 'Submissions' ? selectedSubmissionStatusFilters : []}
              onSubmissionStatusFiltersChange={activeTab === 'Submissions' ? handleSubmissionStatusFiltersChange : () => {}}
            />
          )}
        </div>

        {/* Pagination Controls for Submissions */}
        {activeTab === 'Submissions' && (
          <PaginationControls
            currentPage={submissionsCurrentPage}
            totalPages={submissionsTotalPages}
            totalItems={totalSubmissions}
            itemsPerPage={10}
            onPageChange={(newPage) => setSubmissionsPage(newPage)}
            hasNextPage={submissionsHasNextPage}
            hasPrevPage={submissionsHasPrevPage}
            itemName="submissions"
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          itemTitle={itemToDelete ? getItemTitle(itemToDelete) : ''}
          itemType={activeTab.slice(0, -1)}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          {renderContent()}
        </div>
      </div>

      {/* Forms */}
      {showForm && (
        <>
          {activeTab === 'Lessons' && (
            <LessonForm
              mode={formMode}
              lesson={selectedItem}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
          {activeTab === 'Courses' && (
            <CourseForm
              mode={formMode}
              course={selectedItem}
              lessons={lessons}
              submissionForms={submissionForms}
              reinforcements={reinforcements}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
          {activeTab === 'Questions' && (
            <QuestionForm
              mode={formMode}
              question={selectedItem}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
          {activeTab === 'Events & Information' && (
            <EventForm
              mode={formMode}
              event={selectedItem}
              programs={programs}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
          {activeTab === 'Submission Forms' && (
            <SubmissionFormForm
              mode={formMode}
              submissionForm={selectedItem}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
          {activeTab === 'Submissions' && (
            <SubmissionForm
              mode={formMode}
              submission={selectedItem}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
          {activeTab === 'Reinforcements' && (
            <ReinforcementForm
              mode={formMode}
              reinforcement={selectedItem}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
          {activeTab === 'Tags' && (
            <TagForm
              mode={formMode}
              tag={selectedItem}
              onSubmit={handleFormSubmit}
              onClose={handleFormClose}
            />
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  lesson: state.lesson,
  course: state.course,
  path: state.path,
  question: state.question,
  event: state.event,
  program: state.program,
  submissionForm: state.submissionForm,
  submission: state.submission,
  tag: state.tag,
  reinforcement: state.reinforcement
});

export default connect(
  mapStateToProps,
  {
    getLessons,
    addLesson,
    deleteLesson,
    updateLesson,
    getCourses,
    addCourse,
    deleteCourse,
    updateCourse,
    getPaths,
    addPath,
    deletePath,
    updatePath,
    getQuestions,
    addQuestion,
    deleteQuestion,
    updateQuestion,
    getEvents,
    addEvent,
    deleteEvent,
    updateEvent,
    getPrograms,
    getSubmissionForms,
    addSubmissionForm,
    deleteSubmissionForm,
    updateSubmissionForm,
    getSubmissions,
    addSubmission,
    deleteSubmission,
    updateSubmission,
    reviewSubmission,
    setSubmissionsPage,
    getTags,
    addTag,
    deleteTag,
    updateTag,
    getReinforcements,
    addReinforcement,
    deleteReinforcement,
    updateReinforcement
  }
)(ContentManagementContainer);